import { useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"

import { ExperienceAttendee } from "@bullseye/types"

import { useUserContext } from "../providers"
import { usePostRequest } from "./useRequest"

type ExperienceAttendeeRequest = {
  user_id: string
}

export const useAttendExperience = (experienceId: string) => {
  const { accessToken, user } = useUserContext()
  const queryClient = useQueryClient()
  const { postRequest, postRequestAsync, ...rest } = usePostRequest<
    ExperienceAttendee,
    ExperienceAttendeeRequest
  >({
    path: `v1/experiences/${experienceId}/attendees`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  const attendExperience = useCallback(
    () => postRequest({ body: { user_id: user?.id } }),
    [postRequest, user?.id],
  )

  const attendExperienceAsync = useCallback(
    async () => {
      const result = await postRequestAsync({ body: { user_id: user?.id } })
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["v1/experiences"],
        }),
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0]
            return (
              typeof key === "string" &&
              key.includes("/users/") &&
              key.includes("/experiences")
            )
          },
        }),
      ])
      return result
    },
    [postRequestAsync, user?.id, queryClient],
  )

  return {
    attendExperience,
    attendExperienceAsync,
    ...rest,
  }
}
